import React from 'react';
import { Box } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate } from 'react-router-dom';

import { PageHeader, DataTableCard, FilterChips, OrderDetailDialog } from '../ui';
import buildMyOrdersColumns from './buildMyOrdersColumns';
import useMyOrdersPage from './useMyOrdersPage';

const SORT_OPTIONS = [
  { value: 'newest', labelKey: 'Newest first' },
  { value: 'oldest', labelKey: 'Oldest first' },
  { value: 'price_high', labelKey: 'Price: high to low' },
  { value: 'price_low', labelKey: 'Price: low to high' },
];

const MyOrders = () => {
  const p = useMyOrdersPage();
  const navigate = useNavigate();
  const columns = buildMyOrdersColumns({ t: p.t, i18n: p.i18n, onView: p.setDetailOrder });

  const statusOptions = [
    { value: 'all', label: p.t('All Statuses') },
    { value: 'pending', label: p.t('pending') },
    { value: 'accepted', label: p.t('accepted') },
    { value: 'rejected', label: p.t('rejected') },
    { value: 'paid', label: p.t('paid') },
  ];
  const sortOptions = SORT_OPTIONS.map((o) => ({ value: o.value, label: p.t(o.labelKey) }));

  return (
    <Box>
      <PageHeader
        title={p.t('My Orders')}
        subtitle={p.t('{{count}} orders', { count: p.total, defaultValue: `${p.total} orders` })}
      />

      <DataTableCard
        status={p.effectiveStatus}
        rows={p.effectiveRows}
        columns={columns}
        toolbar={(
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterChips options={statusOptions} value={p.statusFilter} onChange={p.setStatusFilter} filtersLabel={p.t('Filters')} />
            <FilterChips options={sortOptions} value={p.sort} onChange={p.setSort} filtersLabel={p.t('Sort', { defaultValue: 'Sort' })} />
          </Box>
        )}
        empty={{
          kind: 'none',
          icon: ReceiptLongIcon,
          title: p.t("You haven't placed any orders yet.", { defaultValue: "You haven't placed any orders yet." }),
          description: p.t('Orders you place will show up here.', { defaultValue: 'Orders you place will show up here.' }),
          actions: [{ label: p.t('Browse products', { defaultValue: 'Browse products' }), variant: 'contained', onClick: () => navigate('/shop') }],
        }}
        error={{
          title: p.t("Couldn't load orders", { defaultValue: "Couldn't load orders" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: p.fetchOrders,
        }}
        permission={{
          title: p.t("You don't have access to Orders", { defaultValue: "You don't have access to Orders" }),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row._id}
        onRowClick={(params) => p.setDetailOrder(params.row)}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <OrderDetailDialog
        order={p.detailOrder}
        open={Boolean(p.detailOrder)}
        onClose={() => p.setDetailOrder(null)}
        t={p.t}
        i18n={p.i18n}
      />
    </Box>
  );
};

export default MyOrders;
